
import React, {FC} from 'react';
import classNames from 'classnames';
import Button from 'components/Button';
import {Variant} from 'components/Button/variants';
import {RenewInterval} from 'components/checkout/query/types';
import {createPurchase, setupStore} from 'components/checkout/setup';
import {usePrices} from 'components/utils/usePrices';
import {Routes} from 'constants/index';
import Pricing from 'pages/pricing-packages/components/Content/Pricing';
import {useContextConfig} from 'pages/pricing-packages/components/context';

import * as styles from './styles.module.scss';

/**
 * Checks if membership price data is available for a specific interval and quantity
 * @param memberships The price data object
 * @param oneTime The one-time price array
 * @param interval The interval to check
 * @param qty The quantity to check
 * @returns True if the price data is available, false otherwise
 */
const isMembershipPriceAvailable = (
	memberships: { [key: string]: { price: { quantity: number; total: number } }[] },
	oneTime: number[],
	interval: string | undefined,
	qty: number
): boolean => {
	if (interval && memberships) {
		const intervalPrices = memberships[interval];
		return !!(
			intervalPrices &&
			intervalPrices[qty - 1] &&
			intervalPrices[qty - 1].price
		);
	} else {
		return !!(oneTime && oneTime[qty - 1] !== undefined);
	}
};

type PricingProps = {
	center?: boolean,
	children: string,
};

type PricingDetails = {
	interval?: keyof typeof RenewInterval,
	qty: 1 | 2,
};

// This is magic parsing; probably don't do stuff like this
const parseDescription = (text: string): PricingDetails => {
	let interval: (keyof typeof RenewInterval | undefined) = undefined;
	let qty: 1 | 2;

	if (text.includes('month')) {
		interval = 'MONTH';
	} else if (text.includes('quarter')) {
		interval = 'QUARTER';
	} else if (!text.includes('baseline')) {
		throw new Error('Unparseable: missing interval.');
	}

	if (text.startsWith('1')) {
		qty = 1;
	} else if (text.startsWith('2')) {
		qty = 2;
	} else {
		throw new Error('Unparseable: missing quantity.');
	}

	return {interval, qty};
};

const PriceButton: FC<PricingProps> = ({children, center}) => {
	const {interval, qty} = parseDescription(children);
	const {isLoading, hasError, id, memberships, oneTime} = usePrices();
	const config = useContextConfig();

	if (isLoading) {
		return (
			<div className={styles.loading}>
				Loading packages...
			</div>
		);
	}

	if (hasError) {
		return (
			<div className={styles.error}>
				<div>Error loading packages, please refresh the page.</div>
			</div>
		);
	}

	const pricesReady = isMembershipPriceAvailable(memberships, oneTime, interval, qty);

	let total: number = 0;
	if (pricesReady) {
		if (interval && memberships) {
			total = memberships[interval][qty - 1].price.total;
		} else if (oneTime) {
			total = oneTime[qty - 1];
		}
	}

	// Compute & prepare pricing details
	const pricingProps = {
		center,
		qty,
		text: children,
		total,
	};

	// Compute & prepare button actions
	const buttonProps = {
		onClick: () => setupStore(
			config.setup ? (
				config.setup({
					interval: interval ? RenewInterval[interval] : undefined,
					quantity: qty,
				})
			) : (
				createPurchase({
					product: id,
					interval: interval ? RenewInterval[interval] : undefined,
					quantity: qty,
					redirect: Routes.dashboard,
				})
			)
		),
		variant: (qty === 1 ? 'primary' : 'outline') as Variant,
	};

	return (
		<Pricing {...pricingProps}>
			<Button {...buttonProps} className={classNames({
				[styles.mobile]: pricingProps.center
			})}>
				Continue
			</Button>
		</Pricing>
	);
};

export default PriceButton;
